import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRecovery, setIsRecovery] = useState(false);

  const texts = {
    fr: {
      title: 'Nouveau mot de passe',
      password: 'Nouveau mot de passe',
      confirm: 'Confirmer le mot de passe',
      submit: 'Mettre à jour',
      success: 'Mot de passe mis à jour ! Redirection…',
      mismatch: 'Les mots de passe ne correspondent pas',
      noSession: 'Lien invalide ou expiré. Demandez un nouveau lien.',
      genericError: 'Une erreur est survenue',
    },
    en: {
      title: 'New Password',
      password: 'New password',
      confirm: 'Confirm password',
      submit: 'Update',
      success: 'Password updated! Redirecting…',
      mismatch: 'Passwords do not match',
      noSession: 'Invalid or expired link. Please request a new one.',
      genericError: 'An error occurred',
    },
    es: {
      title: 'Nueva contraseña',
      password: 'Nueva contraseña',
      confirm: 'Confirmar contraseña',
      submit: 'Actualizar',
      success: '¡Contraseña actualizada! Redirigiendo…',
      mismatch: 'Las contraseñas no coinciden',
      noSession: 'Enlace inválido o expirado. Solicite uno nuevo.',
      genericError: 'Ocurrió un error',
    },
  };

  const t = texts[language];

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
    });
    // Also check hash for recovery token
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setIsRecovery(true);
    }
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t.mismatch);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(t.genericError + ': ' + error.message);
      } else {
        setSuccess(t.success);
        setTimeout(() => navigate('/'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8"
      >
        <div className="text-center space-y-3">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-primary flex items-center justify-center">
            <Shield size={40} className="text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold font-display">{t.title}</h1>
        </div>

        <form onSubmit={handleSubmit} className="card-elevated p-8 space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t.password}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                placeholder="••••••••"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t.confirm}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-destructive text-sm text-center">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl p-3 text-[hsl(var(--success))] text-sm text-center">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="touch-button-lg w-full bg-primary text-primary-foreground rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              t.submit
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;